import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { CheckCircle, CircleNotch, Warning, Rocket, ArrowRight, Sparkle, DownloadSimple, Confetti } from "@phosphor-icons/react";
import { toast } from "sonner";

function ScoreDonut({ score, level }) {
  const color = level === "ready" ? "hsl(var(--success-foreground))" : level === "attention" ? "hsl(var(--warning-foreground))" : "hsl(var(--destructive))";
  const r = 52, c = 2 * Math.PI * r;
  return (
    <div className="relative grid place-items-center">
      <svg width="140" height="140" className="-rotate-90">
        <circle cx="70" cy="70" r={r} fill="none" stroke="hsl(var(--secondary))" strokeWidth="12" />
        <motion.circle cx="70" cy="70" r={r} fill="none" stroke={color} strokeWidth="12" strokeLinecap="round"
          strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: c - (c * score) / 100 }}
          transition={{ duration: 1.1, ease: "easeOut" }} />
      </svg>
      <div className="absolute text-center">
        <p className="font-display text-3xl font-bold">{score}%</p>
        <p className="text-xs text-muted-foreground">compatible</p>
      </div>
    </div>
  );
}

function Checklist({ items, onDone, testid }) {
  const [visible, setVisible] = useState(0);
  useEffect(() => {
    if (visible < items.length) {
      const t = setTimeout(() => setVisible((v) => v + 1), 650);
      return () => clearTimeout(t);
    }
    if (onDone) {
      const t = setTimeout(onDone, 700);
      return () => clearTimeout(t);
    }
  }, [visible, items.length]); // eslint-disable-line
  return (
    <div className="space-y-3" data-testid={testid}>
      {items.map((it, i) => (
        <AnimatePresence key={i}>
          {i < visible && (
            <motion.div initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ type: "spring", stiffness: 260, damping: 22 }}
              className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3">
              <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.1, type: "spring", stiffness: 500 }}>
                {it.ok === false
                  ? <Warning size={20} weight="fill" className="text-warning-foreground" />
                  : <CheckCircle size={20} weight="fill" className="text-success-foreground" />}
              </motion.span>
              <span className="text-sm font-medium">{it.label}</span>
              {it.value && <span className="ml-auto font-mono text-xs text-muted-foreground">{it.value}</span>}
            </motion.div>
          )}
        </AnimatePresence>
      ))}
    </div>
  );
}

function PhaseHeader({ title, subtitle, spinning }) {
  return (
    <div className="mb-8 text-center">
      <div className="mx-auto mb-5 grid h-16 w-16 place-items-center rounded-2xl bg-secondary">
        {spinning ? <CircleNotch size={30} className="animate-spin" /> : <Sparkle size={30} weight="duotone" />}
      </div>
      <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{title}</h1>
      <p className="mt-2 text-muted-foreground">{subtitle}</p>
    </div>
  );
}

export default function ProjectFlow() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading, analyze, manual, preparing, testing, ready, deploying, live, failed
  const [indep, setIndep] = useState(null);
  const [failInfo, setFailInfo] = useState(null);
  const [showTech, setShowTech] = useState(false);
  const [testStatus, setTestStatus] = useState(null); // status returned by /test; null while awaiting
  const started = useRef(false);

  useEffect(() => {
    api.get(`/projects/${id}`).then((r) => {
      const p = r.data;
      setProject(p);
      if (p.status === "live") { navigate(`/projects/${id}`); return; }
      if (p.compatibility?.level === "manual") setPhase("manual");
      else if (p.status === "ready") setPhase("ready");
      else setPhase("analyze");
    }).catch((e) => { toast.error(apiError(e)); navigate("/dashboard"); });
  }, [id]); // eslint-disable-line

  useEffect(() => {
    if (phase === "ready") loadIndependence();
  }, [phase]); // eslint-disable-line

  const analyzeItems = () => {
    const a = project.analysis || {};
    const items = [];
    if (a.frontend_framework) items.push({ label: "Frontend detected", value: a.frontend_framework });
    if (a.backend_framework) items.push({ label: "Backend detected", value: a.backend_framework });
    if (a.database) items.push({ label: "Database detected", value: a.database });
    if (a.authentication) items.push({ label: "Authentication detected", value: a.authentication });
    if ((a.env_vars || []).length) items.push({ label: "Environment configuration detected" });
    items.push({ label: "Dependencies inspected" });
    items.push({ label: `Hosting requirements detected`, value: `${(a.emergent_findings || []).length} to adapt` });
    return items;
  };

  const doPrepare = async () => {
    setPhase("preparing");
    try {
      const { data } = await api.post(`/projects/${id}/prepare`);
      setProject(data);
    } catch (e) { toast.error(apiError(e)); setPhase("analyze"); }
  };

  const doTest = async () => {
    setTestStatus(null);      // reset so the routing checklist can't fire on stale state
    setPhase("testing");
    try {
      const { data } = await api.post(`/projects/${id}/test`);
      setProject(data);
      setTestStatus(data.status);   // route from the RETURNED status, never stale state
    } catch (e) {
      toast.error(apiError(e));
      setTestStatus("needs_attention");   // deterministic failure -> manual
    }
  };

  const loadIndependence = async () => {
    try { const { data } = await api.get(`/projects/${id}/independence`); setIndep(data); }
    catch (e) { console.error("Independence check failed (non-blocking):", e); }
  };

  const doDeploy = async () => {
    setPhase("deploying");
    try {
      const { data } = await api.post(`/projects/${id}/deploy`, { plan: "starter" });
      setProject(data);
    } catch (e) {
      const detail = apiError(e);
      const tech = e?.response?.data?.technical;
      setFailInfo({ message: detail, technical: tech });
      // refetch to surface stored technical details
      try { const { data } = await api.get(`/projects/${id}`); setProject(data); if (data.deployment?.technical) setFailInfo({ message: data.deployment.error || detail, technical: data.deployment.technical }); } catch (err) { console.error("Failed to refetch deploy failure details:", err); }
      setPhase("failed");
    }
  };

  const download = async () => {
    try {
      const res = await api.get(`/projects/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${project.slug}-independent.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (phase === "loading" || !project) {
    return <Layout><div className="grid min-h-[60vh] place-items-center"><CircleNotch size={32} className="animate-spin text-muted-foreground" /></div></Layout>;
  }

  const compat = project.compatibility || {};

  return (
    <Layout>
      <div className="relative mx-auto max-w-2xl px-6 py-16">
        <div className="blob left-1/2 top-10 h-72 w-72 -translate-x-1/2 bg-accent" />
        <div className="relative z-10">
          <AnimatePresence mode="wait">

            {phase === "analyze" && (
              <motion.div key="analyze" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <PhaseHeader title="Looking at your app…" subtitle="Detecting what your project is built with." />
                <Checklist items={analyzeItems()} testid="analyze-checklist" onDone={() => setPhase("analyze-done")} />
              </motion.div>
            )}

            {phase === "analyze-done" && (
              <motion.div key="analyze-done" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
                <div className="rounded-3xl border border-border bg-card p-8 text-center">
                  <ScoreDonut score={compat.score ?? 90} level={compat.level} />
                  <div className={`mt-5 inline-block rounded-full px-4 py-1.5 text-sm font-semibold ${
                    compat.level === "ready" ? "bg-success text-success-foreground" : "bg-warning text-warning-foreground"}`}>
                    {compat.label}
                  </div>
                  <p className="mx-auto mt-4 max-w-md text-sm text-muted-foreground">
                    {compat.level === "ready" ? "Everything can be migrated automatically." : "Most of this can be migrated automatically. A couple of things may need your approval later."}
                  </p>
                  {(compat.attention || []).length > 0 && (
                    <ul className="mx-auto mt-4 max-w-md space-y-2 text-left">
                      {compat.attention.map((a, i) => (
                        <li key={i} className="flex items-start gap-2 rounded-xl bg-warning/40 px-3 py-2 text-sm text-warning-foreground">
                          <Warning size={16} weight="fill" className="mt-0.5 shrink-0" /> {a}
                        </li>
                      ))}
                    </ul>
                  )}
                  <Button onClick={doPrepare} data-testid="prepare-button" className="mt-7 rounded-full active:scale-95">
                    Prepare independent version <ArrowRight size={16} weight="bold" />
                  </Button>
                </div>
              </motion.div>
            )}

            {phase === "manual" && (
              <motion.div key="manual" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <PhaseHeader title="This one needs a closer look" subtitle="We couldn't automatically prepare this project." />
                <div className="rounded-3xl border border-border bg-card p-8">
                  <ul className="space-y-2">
                    {(compat.blockers || []).map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm"><Warning size={18} weight="fill" className="mt-0.5 text-warning-foreground" /> {b}</li>
                    ))}
                  </ul>
                  <Button variant="outline" onClick={() => navigate("/dashboard")} className="mt-6 rounded-full">Back to dashboard</Button>
                </div>
              </motion.div>
            )}

            {phase === "preparing" && (
              <motion.div key="preparing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <PhaseHeader title="Preparing your independent version…" subtitle="Making your app run anywhere, all on its own." spinning />
                <Checklist testid="prepare-checklist" onDone={doTest} items={[
                  { label: "Hosting configuration prepared" },
                  { label: "Database configuration prepared" },
                  { label: "Deployment package generated" },
                  { label: "Security checks completed" },
                ]} />
              </motion.div>
            )}

            {phase === "testing" && (
              <motion.div key="testing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <PhaseHeader title="Testing your app…" subtitle="Making sure everything works before going live." spinning />
                {testStatus === null ? (
                  /* Awaiting the /test response — animate a placeholder but NEVER
                     transition until the API result is in (prevents stale->manual). */
                  <Checklist key="test-pending" testid="test-checklist-pending"
                    items={[{ label: "Running checks…" }]} />
                ) : (
                  <Checklist key="test-routing" testid="test-checklist"
                    onDone={() => setPhase(testStatus === "ready" ? "ready" : "manual")}
                    items={(project.migration_report?.test_checks || []).map((c) => ({ label: c.label, ok: c.ok }))} />
                )}
              </motion.div>
            )}

            {phase === "ready" && (
              <motion.div key="ready" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
                <div className="rounded-3xl border border-border bg-card p-10 text-center">
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 300 }}
                    className="mx-auto grid h-20 w-20 place-items-center rounded-2xl bg-success">
                    <CheckCircle size={40} weight="fill" className="text-success-foreground" />
                  </motion.div>
                  <h1 className="mt-6 font-display text-4xl font-bold tracking-tight">Your project is ready</h1>
                  <p className="mx-auto mt-3 max-w-md text-muted-foreground">We prepared, adapted and tested your app. Click below to put it online.</p>

                  {indep && (
                    <div className="mx-auto mt-6 max-w-md rounded-2xl border border-border bg-secondary/50 p-5 text-left" data-testid="independence-check">
                      <p className="mb-3 text-sm font-semibold">Independence check</p>
                      <ul className="space-y-2">
                        {indep.checks.map((c, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm">
                            {c.ok ? <CheckCircle size={16} weight="fill" className="text-success-foreground" /> : <Warning size={16} weight="fill" className="text-warning-foreground" />}
                            <span className={c.ok ? "" : "text-warning-foreground"}>{c.label}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <Button onClick={doDeploy} data-testid="deploy-button" size="lg" className="mt-8 rounded-full px-10 py-6 text-base active:scale-95">
                    <Rocket size={20} weight="fill" /> Host my app
                  </Button>
                  <div className="mt-4">
                    <button onClick={download} data-testid="download-button" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
                      <DownloadSimple size={16} /> Download independent version
                    </button>
                  </div>
                  <button onClick={() => navigate(`/projects/${id}`)} className="mt-6 block w-full text-center text-xs text-muted-foreground hover:text-foreground" data-testid="view-report-link">
                    View migration report
                  </button>
                </div>
              </motion.div>
            )}

            {phase === "deploying" && (
              <motion.div key="deploying" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <PhaseHeader title="Putting your app online…" subtitle="Provisioning everything your app needs to run." spinning />
                <Checklist testid="deploy-checklist" onDone={() => setPhase("live")} items={[
                  { label: "Application environment created" },
                  { label: "Independent database created" },
                  { label: "Networking configured" },
                  { label: "HTTPS certificate issued" },
                  { label: "Web address assigned", value: project.deployment?.domain },
                  { label: "Health checks passed" },
                ]} />
              </motion.div>
            )}

            {phase === "live" && (
              <motion.div key="live" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}>
                <div className="relative rounded-3xl border border-border bg-card p-10 text-center">
                  <div className="tracing-beam absolute inset-x-0 top-0 h-1 rounded-t-3xl" />
                  <motion.div initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 260 }}
                    className="mx-auto grid h-20 w-20 place-items-center rounded-2xl bg-success">
                    <Confetti size={40} weight="fill" className="text-success-foreground" />
                  </motion.div>
                  <h1 className="mt-6 font-display text-4xl font-bold tracking-tight sm:text-5xl">Your app is live 🎉</h1>
                  <a href={project.deployment?.url} target="_blank" rel="noreferrer"
                    className="mt-4 inline-block font-mono text-sm text-muted-foreground hover:text-foreground" data-testid="live-url">
                    {project.deployment?.url}
                  </a>
                  {project.deployment?.simulation && (
                    <p className="mx-auto mt-3 max-w-md rounded-xl bg-warning/40 px-3 py-2 text-xs text-warning-foreground">
                      Simulation mode: hosting infrastructure is emulated in this environment. Connect a real provider to serve live traffic.
                    </p>
                  )}
                  <div className="mt-8 flex flex-wrap justify-center gap-3">
                    <Button asChild className="rounded-full active:scale-95" data-testid="open-website-button">
                      <a href={project.deployment?.url} target="_blank" rel="noreferrer">Open website <ArrowRight size={16} weight="bold" /></a>
                    </Button>
                    <Button variant="outline" className="rounded-full" onClick={() => navigate(`/projects/${id}`)} data-testid="manage-app-button">
                      Manage app
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            {phase === "failed" && (
              <motion.div key="failed" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
                <div className="rounded-3xl border border-border bg-card p-10 text-center">
                  <div className="mx-auto grid h-20 w-20 place-items-center rounded-2xl bg-warning">
                    <Warning size={40} weight="fill" className="text-warning-foreground" />
                  </div>
                  <h1 className="mt-6 font-display text-3xl font-bold tracking-tight">We couldn't put your app online yet</h1>
                  <p className="mx-auto mt-3 max-w-md text-muted-foreground" data-testid="deploy-error-message">
                    {failInfo?.message || "Something interrupted the deployment. You can try again."}
                  </p>
                  <div className="mt-8 flex flex-wrap justify-center gap-3">
                    <Button onClick={doDeploy} className="rounded-full active:scale-95" data-testid="retry-deploy-button">
                      <Rocket size={16} weight="fill" /> Try again
                    </Button>
                    <Button variant="outline" className="rounded-full" onClick={() => navigate("/dashboard")}>Back to dashboard</Button>
                  </div>
                  {failInfo?.technical && (
                    <div className="mt-6 text-left">
                      <button onClick={() => setShowTech((s) => !s)} className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline" data-testid="toggle-technical">
                        Advanced → Technical details
                      </button>
                      {showTech && <p className="mt-2 rounded-xl bg-secondary p-3 font-mono text-xs text-muted-foreground">{failInfo.technical}</p>}
                    </div>
                  )}
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>
    </Layout>
  );
}
